import axios from "axios";

// Create a centralized axios instance with base URL logic
const createOpenAIApiInstance = () => {
  return axios.create({ baseURL: '/api3.2/openai' });
};

// Get the OpenAI API instance
const openaiApi = createOpenAIApiInstance();

// Helper function to get X-Forwarded-Host header
const getForwardedHost = (apiUrl) => {
  // If apiUrl is missing or empty, use window.location.host
  if (!apiUrl) {
    return window.location.host;
  }
  
  // For relative URLs (starting with /), use window.location.host directly
  if (apiUrl.startsWith("/")) {
    return window.location.host;
  }
  
  // For absolute URLs, parse them to extract the host
  try {
    const parsedUrl = new URL(apiUrl);
    // Ensure we always return a valid host, fallback to window.location.host if empty
    return parsedUrl.host || window.location.host;
  } catch (error) {
    // If parsing fails, fallback to window.location.host
    return window.location.host;
  }
};

/**
 * Generate AI response using OpenAI API
 * @param {string} message - The message to send to OpenAI
 * @param {string} systemPrompt - Optional system prompt to guide the AI
 * @param {string} model - The OpenAI model to use (default: "gpt-4o")
 * @param {number} temperature - The temperature for randomness (default: 0.7)
 * @param {number} maxTokens - Maximum tokens in response (default: 1000)
 * @returns {Promise<Object>} The OpenAI API response
 */
export async function generateAIResponse(
  message,
  systemPrompt = "",
  model = "gpt-4o",
  temperature = 0.7,
  maxTokens = 1000
) {
  if (!message || typeof message !== 'string') {
    throw new Error("Message is required and must be a string!");
  }

  try {
    const response = await openaiApi.post(
      "/generate",
      {
        message,
        systemPrompt,
        model,
        temperature,
        maxTokens,
      },
      {
        headers: {
          "Content-Type": "application/json",
          "X-Forwarded-Host": getForwardedHost(openaiApi.defaults.baseURL),
        },
      }
    );

    console.log("✅ AI Response Generated:", response.data);
    return response.data;
  } catch (error) {
    console.error("🚨 Error generating AI response:", error);
    throw error;
  }
}

/**
 * Improve comment text with AI - better grammar, formatting, and structure
 * @param {string} text - The comment text to improve
 * @param {number} maxTokens - Maximum tokens for the improved comment (default: 500)
 * @returns {Promise<string>} The improved comment
 */
export async function improveComment(text, maxTokens = 500) {
  if (!text || typeof text !== 'string' || text.trim().length === 0) {
    throw new Error("Text to improve is required and cannot be empty!");
  }

  try {
    const message = `Improve this timesheet comment: ${text.trim()}`;
    const systemPrompt = `You are an assistant that improves technical timesheet comments for professional documentation.

Context: These are timesheet entries describing technical work completed. They need to be clear, well-formatted, and professional. The input may be raw typed notes OR pasted email content.

Email Content Handling:
- If the input appears to be an email or email chain, preserve ALL messages in the chain
- Do NOT remove or summarize any email — keep the full conversation
- Strip only email signatures, confidentiality notices, and legal boilerplate
- Convert the email content into a clean, well-formatted timesheet comment while keeping all messages

Requirements:
- Add a one-sentence summary at the very top
- Fix grammar and spelling errors
- Flesh out abbreviated details where appropriate
- Use professional, clear language
- Format using the specified structure below
- Keep technical accuracy while improving readability
- NEVER remove or omit the primary content — when in doubt, keep it

Formatting Rules:
- Titles should use == Title == format
- Bullet points should use " - " (space dash space)
- Use clear section headers when appropriate
- Regular sentences are fine between sections
- Maintain logical flow and organization
- If the content contains code snippets, preserve them exactly as-is — do not rewrite or alter code
`;
    
    const response = await generateAIResponse(
      message,
      systemPrompt,
      "gpt-4o",
      0.4, // Slightly higher temperature for more creative improvements
      maxTokens
    );

    if (response.success && response.data && response.data.response) {
      let improvedText = response.data.response.trim();
      
      // Remove surrounding quotes if present
      if ((improvedText.startsWith('"') && improvedText.endsWith('"')) ||
          (improvedText.startsWith("'") && improvedText.endsWith("'"))) {
        improvedText = improvedText.slice(1, -1).trim();
      }
      
      return improvedText;
    } else {
      throw new Error("Invalid response format from AI service");
    }
  } catch (error) {
    console.error("🚨 Error improving comment:", error);
    throw error;
  }
}

/**
 * Format raw text/email content into clean HTML for a rich text editor.
 * @param {string} text - The raw content to format
 * @param {number} maxTokens - Maximum tokens for the response (default: 2000)
 * @returns {Promise<string>} Clean HTML string
 */
export async function formatContentAsHTML(text, maxTokens = 2000) {
  if (!text || typeof text !== 'string' || text.trim().length === 0) {
    throw new Error("Text to format is required and cannot be empty!");
  }

  try {
    const message = `Format this content into clean HTML:\n\n${text.trim()}`;
    const systemPrompt = `You are an assistant that converts raw text, pasted emails, and unformatted content into clean, well-structured HTML for a rich text editor.

Email Chain Handling (CRITICAL):
- Preserve ALL emails in the chain — every message from every person, in order
- Do NOT remove or summarize any email in the chain — the user wants the full conversation
- Format each email clearly with the sender name, date, and their message body
- Use a visual separator (like <hr> or <strong> header) between each email in the chain
- Strip ONLY: email signatures (name/title/phone blocks, "-- " delimiters), confidentiality/legal disclaimers, and excessive whitespace
- Keep the "From/Sent/To/Subject" headers as formatted metadata for each email — they provide useful context
- When in doubt, KEEP the content rather than removing it

General Formatting:
- Return ONLY the HTML content, no markdown, no code fences, no explanation
- Convert dash/asterisk/number lists into proper <ul>/<ol> with <li> elements
- When data is clearly tabular (columns of related info), use <table> with <thead> and <tbody>
- Preserve meaningful line breaks as <p> tags
- Use <strong> for emphasis where appropriate (e.g. section headers in the original text)
- Clean up excessive whitespace and formatting artifacts
- Keep all meaningful content — do not summarize or shorten, just reformat
- Use simple, inline-style-free HTML that works in TinyMCE

Code Snippets:
- When content contains code (SQL, JavaScript, C#, Python, HTML, CSS, shell commands, config files, etc.), wrap it in <pre><code class="language-LANG">...</code></pre> where LANG is the language (e.g. language-sql, language-javascript, language-csharp, language-python)
- Preserve code formatting exactly — do not rewrite, simplify, or alter the code
- If code appears inline within a sentence, use <code>...</code> instead`;

    const response = await generateAIResponse(
      message,
      systemPrompt,
      "gpt-4o",
      0.2,
      maxTokens
    );

    if (response.success && response.data && response.data.response) {
      let html = response.data.response.trim();
      if (html.startsWith("```html")) {
        html = html.slice(7);
      } else if (html.startsWith("```")) {
        html = html.slice(3);
      }
      if (html.endsWith("```")) {
        html = html.slice(0, -3);
      }
      return html.trim();
    } else {
      throw new Error("Invalid response format from AI service");
    }
  } catch (error) {
    console.error("Error formatting content as HTML:", error);
    throw error;
  }
}

/**
 * Match old activity names to new activity names using AI semantic understanding.
 * @param {Array<{id: number, title: string}>} oldActivities - Activities to match from
 * @param {Array<{id: number, title: string}>} newActivities - Available target activities
 * @returns {Promise<Record<number, number>>} Mapping of old activity ID → new activity ID
 */
export async function matchActivitiesWithAI(oldActivities, newActivities) {
  if (!oldActivities?.length || !newActivities?.length) return {};

  const message = `Match these source activities to the most appropriate target activity.

Source activities:
${oldActivities.map((a) => `- [ID:${a.id}] "${a.title}"`).join('\n')}

Target activities:
${newActivities.map((a) => `- [ID:${a.id}] "${a.title}"`).join('\n')}

Return ONLY a JSON object mapping source IDs to target IDs, like: {"1": 5, "2": 5}
If a source has no reasonable match, omit it from the result.`;

  const systemPrompt = `You are a business process mapping assistant. You match activity names between two sets based on semantic similarity.

Rules:
- Match activities that represent the same type of work, even if named differently (e.g. "Dev" ↔ "Development", "QA" ↔ "Testing", "Meetings" ↔ "Meeting")
- If there's only one target activity, map everything to it
- Only return a valid JSON object, no explanation, no markdown fences
- Be generous with matching — partial matches are better than no match`;

  try {
    const response = await generateAIResponse(message, systemPrompt, "gpt-4o", 0.1, 200);
    if (response.success && response.data?.response) {
      let text = response.data.response.trim();
      // Strip markdown fences if present
      if (text.startsWith('```')) text = text.replace(/```json?\n?/g, '').replace(/```$/g, '').trim();
      const parsed = JSON.parse(text);
      // Convert string keys to numbers
      const result = {};
      for (const [oldId, newId] of Object.entries(parsed)) {
        result[Number(oldId)] = Number(newId);
      }
      return result;
    }
    return {};
  } catch (error) {
    console.error('Error matching activities with AI:', error);
    return {};
  }
}

/**
 * Generate a summary from the provided text using AI
 * @param {string} text - The text to summarize
 * @param {number} maxTokens - Maximum tokens for the summary (default: 150)
 * @returns {Promise<string>} The generated summary
 */
export async function generateSummary(text, maxTokens = 150) {
  if (!text || typeof text !== 'string' || text.trim().length === 0) {
    throw new Error("Text to summarize is required and cannot be empty!");
  }

  try {
    const message = `Summarize this timesheet entry: ${text.trim()}`;
    const systemPrompt = `You are an assistant that creates concise summaries of technical timesheet entries for managers.

Context: Each entry represents a task/work item with technical implementation details. The input may be raw notes or pasted email content. Managers need to understand what was accomplished at a glance.

Requirements:
- Maximum 180 characters
- Use clear, manager-friendly language
- Be specific about what was delivered/accomplished
- If the input is an email chain, summarize the overall conversation topic and outcome`;
    
    const response = await generateAIResponse(
      message,
      systemPrompt,
      "gpt-4o",
      0.3, // Lower temperature for more consistent summaries
      50 // Reduced max tokens for shorter summaries
    );

    if (response.success && response.data && response.data.response) {
      return response.data.response.trim();
    } else {
      throw new Error("Invalid response format from AI service");
    }
  } catch (error) {
    console.error("🚨 Error generating summary:", error);
    throw error;
  }
}
